import type { SearchKeyValueParam } from '../../types';
import { canonicalizeKnownFacetId } from './facet-id';

/** Browser URL query key for the search term. */
export const SEARCH_URL_Q = 'q';

/** Empty browser query — landing / show-all omits `q` from the URL. */
export const SEARCH_SHOW_ALL_QUERY = '';

/** Funnelback `query` value when browsing all results (empty user search). */
export const FUNNELBACK_SHOW_ALL_QUERY = '!showall';

/** Funnelback `search.json` parameter for the search term. */
export const FUNNELBACK_LOOKUP_QUERY_PARAM = 'query';

/** Query string key for results page (1-based). Omitted from URL when page is 1. */
export const SEARCH_URL_PAGE = 'page';

/**
 * Prefix for facet keys in the browser URL (`f.<facetId>=comma,separated`).
 */
export const SEARCH_URL_FACET_KEY_PREFIX = 'f.';

export type HydratedSearchUrl = {
  query: string;
  /** 1-based results page from URL. */
  page: number;
  selectedFacets: Record<string, string[]>;
};

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * Read search UI state from a URL query string (e.g. `window.location.search`).
 */
export function parseSearchParamsFromSearch(search: string): HydratedSearchUrl {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  const query = params.get(SEARCH_URL_Q) ?? '';
  const pageRaw = params.get(SEARCH_URL_PAGE);
  let page = 1;
  if (pageRaw) {
    const n = Number.parseInt(pageRaw, 10);
    if (Number.isFinite(n) && n >= 1) {
      page = n;
    }
  }

  const selectedFacets: Record<string, string[]> = {};
  params.forEach((value, key) => {
    if (key === SEARCH_URL_Q || key === SEARCH_URL_PAGE) {
      return;
    }
    if (!key.startsWith(SEARCH_URL_FACET_KEY_PREFIX)) {
      return;
    }
    const facetId = canonicalizeKnownFacetId(
      key.slice(SEARCH_URL_FACET_KEY_PREFIX.length),
    );
    if (!facetId) {
      return;
    }
    const parts = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length > 0) {
      const existing = selectedFacets[facetId] ?? [];
      selectedFacets[facetId] = [...new Set([...existing, ...parts])];
    }
  });

  return { query, page, selectedFacets };
}

export type SearchUrlSerializeInput = {
  query: string;
  /** 1-based results page; omitted from the URL when 1. */
  page: number;
  selectedFacets: Record<string, string[]>;
  /** Extra static params to preserve (e.g. Matrix passthrough). */
  preserveParams?: URLSearchParams;
};

/**
 * Build a query string (no leading `?`) for the current search state.
 */
export function serializeSearchParamsToQueryString(
  input: SearchUrlSerializeInput,
): string {
  const params = new URLSearchParams();

  if (input.preserveParams) {
    input.preserveParams.forEach((value, key) => {
      if (
        key === SEARCH_URL_Q ||
        key === SEARCH_URL_PAGE ||
        key.startsWith(SEARCH_URL_FACET_KEY_PREFIX)
      ) {
        return;
      }
      params.append(key, value);
    });
  }

  if (input.query.trim()) {
    params.set(SEARCH_URL_Q, input.query.trim());
  }

  for (const [facetId, values] of Object.entries(input.selectedFacets)) {
    if (!values?.length) {
      continue;
    }
    const key = `${SEARCH_URL_FACET_KEY_PREFIX}${facetId}`;
    params.set(key, values.join(','));
  }

  if (input.page > 1) {
    params.set(SEARCH_URL_PAGE, String(input.page));
  }

  return params.toString();
}

/**
 * Replace the current history entry's query string without reloading.
 */
export function replaceHistorySearchParams(queryString: string): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) {
    return;
  }
  const url = new URL(window.location.href);
  url.search = queryString ? `?${queryString}` : '';
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Subscribe to browser back/forward; returns unsubscribe.
 */
export function subscribePopState(handler: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  window.addEventListener('popstate', handler);
  return () => window.removeEventListener('popstate', handler);
}

/** Ensure `fmt=json++` for programmatic Funnelback search when not already present. */
export function mergeLookupExtrasWithDefaults(
  base: SearchKeyValueParam[],
): SearchKeyValueParam[] {
  const extras = [...base];
  if (!extras.some((p) => p.key === 'fmt')) {
    extras.push({ key: 'fmt', value: 'json++' });
  }
  return extras;
}

/**
 * Merge lookup extras: keys in `override` replace the same key in `base`.
 */
export function mergeLookupKeyValueParams(
  base: SearchKeyValueParam[],
  override: SearchKeyValueParam[],
): SearchKeyValueParam[] {
  const map = new Map<string, string>();
  for (const { key, value } of base) {
    map.set(key, value);
  }
  for (const { key, value } of override) {
    map.set(key, value);
  }
  return Array.from(map.entries()).map(([key, value]) => ({ key, value }));
}

export function buildFacetParams(
  selectedFacets: Record<string, string[]>,
): SearchKeyValueParam[] {
  const out: SearchKeyValueParam[] = [];
  for (const [facetId, values] of Object.entries(selectedFacets)) {
    if (!values?.length) continue;
    const key = `${SEARCH_URL_FACET_KEY_PREFIX}${facetId}`;
    for (const v of values) {
      if (isNonEmptyString(v)) {
        out.push({ key, value: v });
      }
    }
  }
  return out;
}
